import { PaymentsAppService } from './core/payments-app.service';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Payment, PaymentStatus, PaymentType } from '../schemas/payment';
import { createItem, readItems, updateItem, aggregate } from '@directus/sdk';
import { HandleErrorsService } from '../utils/services/handle-errors.service';
import { Duration } from '../schemas/duration.enum';
import { RequestsService } from '../utils/services/requests.service';
import { requiresInit } from '../utils/functions/requires-init';
import { OrgPaymentsService } from './payments.service';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private handleErrorsService = inject(HandleErrorsService);
  private requestsService = inject(RequestsService);
  private orgPaymentsService = inject(OrgPaymentsService);
  private paymentsAppService = inject(PaymentsAppService);
  private paymentsAppInstance = this.paymentsAppService.paymentsAppInstance;

  payments = signal<Payment[]>([]);
  loadedPayments = signal<boolean>(false);
  latestPayment = signal<Payment | null>(null);
  latestPaymentEffect = effect(() => {
    const latestPayment = this.latestPayment();
    const allPayments = this.payments();
    if (latestPayment) {
      const currentPayments = this.payments(); // Get current value of the signal
      const foundIndex = currentPayments.findIndex(
        (payment) => payment.id === latestPayment.id, // Use ID for matching
      );

      let updatedPayments;
      if (foundIndex > -1) {
        updatedPayments = currentPayments.map((payment, index) =>
          index === foundIndex ? latestPayment : payment,
        );
      } else {
        // If not found, add to the beginning (or end, depending on desired behavior for new items)
        updatedPayments = [latestPayment, ...currentPayments];
      }
      this.payments.set(updatedPayments); // Set with a new array reference
    }
    // It's important to reset latestPayment to null *after* processing,
    // but ensure this effect doesn't re-trigger if payments itself is what caused latestPayment to be set.
    // The current placement is okay, but if issues arise, consider { allowSignalWrites: true } for the effect
    // or a more refined way to prevent cycles if latestPayment is set in response to a payments change.
    // For now, this structure is standard.
    this.latestPayment.set(null);
  });
  paymentCount = signal<number>(0);

  // Active filter states - to be set by components via a new method
  activeDurationFilter = signal<Duration>(Duration.WEEK);
  activeCustomStartDate = signal<Date | null>(null);
  activeCustomEndDate = signal<Date | null>(null);
  activeStatusFilter = signal<PaymentStatus | null>(null); // New
  activeAccountFilter = signal<string | null>(null); // New (for receiver_account ID)

  // Removed today, sevenDaysAgo, startOfDay, endOfDay signals as they are now dynamic

  isLoginEffect = effect(() => {
    if (!this.paymentsAppService.accountIsLogin()) {
      this.clearAllStates();
    }
  });

  constructor() {
    const initWrapper = <T extends (...args: any[]) => any>(fn: T) =>
      requiresInit(fn, {
        hasInit: () => this.paymentsAppService.hasInitPaymentsApp(),
        init: () => this.paymentsAppService.init(),
        hasActiveAppOrg: () => this.orgPaymentsService.hasPaymentsAppOrg(),
        initOrg: () => this.orgPaymentsService.initPaymentsOrg(),
      });

    this.createPayment = initWrapper(this.createPayment.bind(this));
    this.updatePayment = initWrapper(this.updatePayment.bind(this));
    this.getSinglePayment = initWrapper(this.getSinglePayment.bind(this));
    this.getPaginatedPayments = initWrapper(
      this.getPaginatedPayments.bind(this),
    );
    // Rename applyDurationFilter to applyFilters and update its wrapping
    this.applyFilters = initWrapper(this.applyFilters.bind(this));

    // Effect to fetch payments when active filters change
    effect(() => {
      // This effect should only run if the service has been initialized
      // and there's an active org. The initWrapper on applyDurationFilter
      // helps ensure this, but an explicit check here is also good.
      if (
        this.paymentsAppService.hasInitPaymentsApp() &&
        this.orgPaymentsService.hasPaymentsAppOrg()
      ) {
        this._fetchPaymentsWithCurrentFilters();
      }
    });
  }

  /**
   * Public method for components to set all desired filters.
   * This will trigger the internal effect to refetch payments.
   */
  applyFilters(filters: {
    duration?: Duration;
    customStart?: Date | null;
    customEnd?: Date | null;
    status?: PaymentStatus | null;
    accountId?: string | null;
  }): void {
    if (filters.duration !== undefined) {
      this.activeDurationFilter.set(filters.duration);
    }
    // Allow unsetting custom dates if not provided or explicitly null
    this.activeCustomStartDate.set(
      filters.customStart === undefined
        ? this.activeCustomStartDate()
        : filters.customStart || null,
    );
    this.activeCustomEndDate.set(
      filters.customEnd === undefined
        ? this.activeCustomEndDate()
        : filters.customEnd || null,
    );

    if (filters.status !== undefined) {
      this.activeStatusFilter.set(filters.status);
    }
    if (filters.accountId !== undefined) {
      this.activeAccountFilter.set(filters.accountId);
    }
    // The internal effect will now pick up these changes and fetch data.
  }

  private async _fetchPaymentsWithCurrentFilters(): Promise<void> {
    this.loadedPayments.set(false);
    let startDateString: string | undefined;
    let endDateString: string | undefined;
    const today = new Date();

    const getISODateString = (date: Date, atStartOfDay: boolean): string => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}${atStartOfDay ? 'T00:00:00.000Z' : 'T23:59:59.999Z'}`;
    };

    const customStart = this.activeCustomStartDate();
    const customEnd = this.activeCustomEndDate();

    if (customStart && customEnd) {
      startDateString = getISODateString(customStart, true);
      endDateString = getISODateString(customEnd, false);
    } else {
      const selectedDuration = this.activeDurationFilter();
      switch (selectedDuration) {
        case Duration.TODAY:
          // "Last 24 hours from now"
          const now = new Date(); // Use 'now' for clarity instead of 'today' for this specific case
          endDateString = now.toISOString();
          const twentyFourHoursAgo = new Date(
            now.getTime() - 24 * 60 * 60 * 1000,
          );
          startDateString = twentyFourHoursAgo.toISOString();
          break;
        case Duration.WEEK:
          const weekAgo = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
          startDateString = getISODateString(weekAgo, true);
          endDateString = getISODateString(today, false);
          break;
        case Duration.MONTH:
          const thirtyDaysAgo = new Date(
            today.getTime() - 29 * 24 * 60 * 60 * 1000,
          );
          startDateString = getISODateString(thirtyDaysAgo, true);
          endDateString = getISODateString(today, false);
          break;
        default:
          // Default to week if something unexpected happens
          const defaultWeekAgo = new Date(
            today.getTime() - 6 * 24 * 60 * 60 * 1000,
          );
          startDateString = getISODateString(defaultWeekAgo, true);
          endDateString = getISODateString(today, false);
      }
    }

    if (!startDateString || !endDateString) {
      console.error(
        'PaymentService: Invalid date range for fetching payments.',
      );
      this.loadedPayments.set(true); // Set to true to stop loading spinners
      this.payments.set([]);
      this.paymentCount.set(0);
      return;
    }

    try {
      // For "last 24 hours", the date_created filter is straightforward.
      // For other cases (WEEK, MONTH, CUSTOM), it uses the getISODateString which sets specific times.
      // The existing directusFilter construction for date_created using _gte and _lte is fine for all these.
      const directusFilter: any = {
        date_created: { _gte: startDateString, _lte: endDateString },
      };

      const currentStatus = this.activeStatusFilter();
      if (currentStatus && currentStatus !== PaymentStatus.ALL) {
        directusFilter.status = { _eq: currentStatus };
      }

      const currentAccountId = this.activeAccountFilter();
      if (currentAccountId && currentAccountId !== 'all') {
        // Assuming 'all' means no account filter
        directusFilter.receiver_account = { _eq: currentAccountId };
      }

      // Fetch total payment count with the calculated filters
      const countResult = await this.requestsService.handleRequest(() =>
        this.paymentsAppInstance.request(
          readItems('payments', {
            aggregate: { count: '*' },
            filter: directusFilter,
          }),
        ),
      );
      this.paymentCount.set(countResult[0]?.count || 0);

      // Fetch payments with the calculated filters
      const paymentsResult = await this.requestsService.handleRequest(() =>
        this.paymentsAppInstance.request(
          readItems('payments', {
            fields: ['*.*.*'],
            sort: '-date_created',
            filter: directusFilter,
          }),
        ),
      );

      if (paymentsResult.length === 0) {
        this.payments.set([]);
      } else {
        this.payments.set(paymentsResult as Payment[]);
      }
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.payments.set([]);
      this.paymentCount.set(0);
    } finally {
      this.loadedPayments.set(true);
    }
  }

  async createPayment(payment: Payment) {
    this.loadedPayments.set(false);
    const { ...data } = payment;
    try {
      const paymentResponse = await this.requestsService.handleRequest(() =>
        this.paymentsAppInstance.request(createItem('payments', data)),
      );
      await this.getSinglePayment(paymentResponse);
      this.loadedPayments.set(true);
      return paymentResponse as Payment;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      return null;
    }
  }

  async updatePayment(payment: Payment) {
    this.loadedPayments.set(false);
    const { id, ...data } = payment;

    try {
      const paymentResponse = await this.requestsService.handleRequest(() =>
        this.paymentsAppInstance.request(
          updateItem('payments', payment.id!, data),
        ),
      );

      await this.getSinglePayment(paymentResponse);
      this.loadedPayments.set(true);
      return paymentResponse as Payment;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      return null;
    }
  }

  // getTotalPaymentCount is removed as its logic is now part of _fetchPaymentsWithCurrentFilters
  // async getTotalPaymentCount ...

  async getSinglePayment(params: Payment): Promise<Payment | null> {
    this.loadedPayments.set(false);
    const { code, id } = params;
    try {
      const payments = await this.requestsService.handleRequest(() =>
        this.paymentsAppInstance.request(
          readItems('payments', {
            filter: {
              id: {
                _eq: id,
              },
              code: {
                _eq: code,
              },
            },
            fields: ['*.*.*'],
          }),
        ),
      );

      if (payments.length === 0) {
        this.loadedPayments.set(true);
        return null;
      }
      this.latestPayment.set(payments[0] as Payment);
      this.loadedPayments.set(true);
      return payments[0] as Payment;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      return null;
    }
  }

  // getPayments is removed as its logic is now part of _fetchPaymentsWithCurrentFilters

  async getPaginatedPayments(page: number, limit: number): Promise<Payment[]> {
    this.loadedPayments.set(false);
    try {
      const payments = await this.requestsService.handleRequest(() =>
        this.paymentsAppInstance.request(
          readItems('payments', {
            fields: ['*.*.*'],
            sort: '-date_created',
            limit: 10,
          }),
        ),
      );

      if (payments.length === 0) {
        this.loadedPayments.set(true);
        return [];
      }
      this.payments.set(payments as Payment[]);
      this.loadedPayments.set(true);
      return payments as Payment[];
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      return [];
    }
  }

  async generatePayments(): Promise<Payment[]> {
    const payments: Payment[] = [];
    for (let i = 0; i < 50; i++) {
      payments.push({
        id: `${i}`,
        amount: (Math.random() * 100000).toFixed(0),
        code: `TRX${i}`,
        label: 'Cash In',
        type: this.getRandomPaymentType(),
        status: this.getRandomPaymentStatus(),
        sender_status: this.getRandomPaymentStatus(),
        receiver_status: this.getRandomPaymentStatus(),
        sender_account: '',
        receiver_account: '',
        sender: '',
        receiver: '',
        date_created: new Date().toISOString(),
        date_updated: new Date().toISOString(),
      });
    }
    return payments;
  }

  private getRandomPaymentType(): Payment['type'] {
    const types = Object.values(PaymentType);
    return types[Math.floor(Math.random() * types.length)];
  }

  private getRandomPaymentStatus(): Payment['status'] {
    const statuses = Object.values(PaymentStatus).filter(
      (status) =>
        status !== PaymentStatus.ALL && status !== PaymentStatus.CANCELED,
    );
    return statuses[Math.floor(Math.random() * statuses.length)];
  }

  clearAllStates() {
    this.payments.set([]);
    this.loadedPayments.set(false);
    this.latestPayment.set(null);
    this.paymentCount.set(0);
  }
}
